import React, { useCallback, useState, useRef, useEffect } from 'react';
import TextInput from '../TextInput/TextInput';
import type { Props as TextInputProps } from '../TextInput/TextInput';
import { useInputComponentIdAndLabelData } from '../utils';
import InputControl from '../InputControl/InputControl';
import Box from '../Box/Box';

const isNumberOrEmptry = (value: string): boolean =>
  /^\d+$/.test(value) || value === '';

const parseDay = (dayString: string): string | null => {
  try {
    if (!isNumberOrEmptry(dayString)) {
      return null;
    }
    const day = parseInt(dayString, 10);
    if (day >= 1 && day <= 31) {
      return day.toString();
    }
    return null;
  } catch (e) {
    return null;
  }
};

const parseMonth = (monthString: string): string | null => {
  try {
    if (!isNumberOrEmptry(monthString)) {
      return null;
    }

    const monthParsed = parseInt(monthString, 10);
    if (monthParsed >= 1 && monthParsed <= 12) {
      return monthParsed.toString();
    }
    return null;
  } catch (e) {
    return null;
  }
};

const parseYear = (yearString: string): string | null => {
  try {
    if (!isNumberOrEmptry(yearString)) {
      return null;
    }
    const yearParsed = parseInt(yearString, 10);
    if (yearString?.length === 4 && yearParsed > 1900) {
      return yearParsed.toString();
    }
    return null;
  } catch (e) {
    return null;
  }
};

const prependZero = (value: string): string =>
  value.length === 1 ? `0${value}` : value;

export type DateInputSeparateFieldsProps = TextInputProps;

const DateInputSeparateFields: React.FC<DateInputSeparateFieldsProps> = (
  props
) => {
  const {
    value = '',
    error,
    onChange,
    label,
    showRequiredAsterisk,
    'aria-label': ariaLabel,
    hint,
    onHintToggle,
    eventId,
    styleInputContainer,
  } = props;

  const [year, setYear] = useState<string>(
    prependZero(parseYear(value.split('-')[0]) || '')
  );
  const [month, setMonth] = useState<string>(
    prependZero(parseMonth(value.split('-')[1]) || '')
  );
  const [day, setDay] = useState<string>(
    prependZero(parseDay(value.split('-')[2]) || '')
  );

  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const onChangeWithParsedValues = useCallback(
    (yyyy: string, mm: string, dd: string) => {
      if (onChange)
        onChange(
          `${parseYear(yyyy) || ''}-${prependZero(
            parseMonth(mm) || ''
          )}-${prependZero(parseDay(dd) || '')}`
        );
    },
    [onChange]
  );

  const onChangeDayFn = useCallback(
    (newDay: string) => {
      if (isNumberOrEmptry(newDay)) {
        setDay(newDay);
        onChangeWithParsedValues(year, month, newDay);
      }
    },
    [year, month, onChangeWithParsedValues]
  );

  const onBlurDayFn = useCallback(() => {
    const parsedDay = prependZero(parseDay(day) ?? '');
    setDay(parsedDay);
    onChangeWithParsedValues(year, month, parsedDay);
  }, [day, year, month, onChangeWithParsedValues]);

  const onChangeMonthFn = useCallback(
    (newMonth: string) => {
      if (isNumberOrEmptry(newMonth)) {
        setMonth(newMonth);
        onChangeWithParsedValues(year, newMonth, day);
      }
    },
    [year, day, onChangeWithParsedValues]
  );

  const onBlurMonthFn = useCallback(() => {
    const parsedMonth = prependZero(parseMonth(month) ?? '');
    setMonth(parsedMonth);
    onChangeWithParsedValues(year, parsedMonth, day);
  }, [month, year, day, onChangeWithParsedValues]);

  const onChangeYearFn = useCallback(
    (newYear: string) => {
      if (isNumberOrEmptry(newYear)) {
        setYear(newYear);
        onChangeWithParsedValues(newYear, month, day);
      }
    },
    [month, day, onChangeWithParsedValues]
  );

  const onBlurYearFn = useCallback(() => {
    setYear(parseYear(year) || '');
    onChangeWithParsedValues(parseYear(year) || '', month, day);
  }, [year, month, day, onChangeWithParsedValues]);

  useEffect(() => {
    if (parseDay(day) && day.length === 2) {
      yearRef.current?.focus();
    }
  }, [day]);

  useEffect(() => {
    if (parseMonth(month) && month.length === 2) {
      dayRef.current?.focus();
    }
  }, [month]);

  // focus on initial render
  useEffect(() => {
    monthRef.current?.focus();
  }, []);

  const { containerId, labelId, hintId } = useInputComponentIdAndLabelData(
    'TextInput',
    label,
    ariaLabel
  );

  return (
    <InputControl
      id={containerId}
      label={label}
      labelId={labelId}
      errorMessage={error}
      showRequiredAsterisk={showRequiredAsterisk}
      hint={hint}
      hintId={hintId}
      onHintToggle={onHintToggle}
      eventId={eventId}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sx={styleInputContainer as any}
      data-testid="DateInputSeparateFields"
    >
      <Box
        sx={{
          display: 'flex',
          gap: '20px',
        }}
      >
        <TextInput
          ref={monthRef}
          onChange={onChangeMonthFn}
          onBlur={onBlurMonthFn}
          value={month}
          label={undefined}
          placeholder="MM"
          maxLength={2}
          inputMode="numeric"
          data-testid="DateInputSeparateFieldsMonth"
          styleInputContainer={{
            flex: 1,
            minWidth: '0',
            maxWidth: '100%',
          }}
          styleInput={{
            maxWidth: '100%',
          }}
        />
        <TextInput
          ref={dayRef}
          onChange={onChangeDayFn}
          onBlur={onBlurDayFn}
          value={day}
          label={undefined}
          placeholder="DD"
          maxLength={2}
          inputMode="numeric"
          data-testid="DateInputSeparateFieldsDay"
          styleInputContainer={{
            flex: 1,
            minWidth: '0',
            maxWidth: '100%',
          }}
          styleInput={{
            maxWidth: '100%',
          }}
        />
        <TextInput
          ref={yearRef}
          onChange={onChangeYearFn}
          onBlur={onBlurYearFn}
          value={year}
          label={undefined}
          placeholder="YYYY"
          maxLength={4}
          inputMode="numeric"
          data-testid="DateInputSeparateFieldsYear"
          styleInputContainer={{
            flex: 1,
            minWidth: '0',
            maxWidth: '100%',
          }}
          styleInput={{
            maxWidth: '100%',
          }}
        />
      </Box>
    </InputControl>
  );
};

export default DateInputSeparateFields;
